/**
 * Local shadcn-style primitives.
 *
 * ⚠️ TEMPORARY SHIM. `@most/ui` is the shared shadcn kit + theme for all three SPAs and is being
 * authored in parallel. These primitives exist so this app compiles and runs standalone today;
 * they intentionally use the SAME prop shapes and the same CSS-variable theme contract, so
 * swapping them out is a change of import path in `src/components/ui/index.ts` and nothing else.
 *
 * Do NOT grow this file. Anything genuinely shared belongs in `@most/ui`, and this app must not
 * edit `packages/`.
 */
import * as React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ── Button ──────────────────────────────────────────────────────────────────────────────────
const buttonVariants = cva(
  // Matches the shared kit (`packages/ui`): the primary button is dark ink ON butter yellow —
  // never white on yellow, which fails AA. `active:translate-y-px` is the cheapest possible
  // "the click registered" on a screen where every button fires a mutation.
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:translate-y-px',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background text-foreground hover:border-muted-foreground/40 hover:bg-muted',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
        ghost: 'text-foreground/80 hover:bg-muted hover:text-foreground',
        // `link` is the interactive-text ink, NOT `primary` — yellow type on white is unreadable.
        link: 'text-link underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-lg px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      // Disable while a request is in flight — but NEVER as a substitute for showing which field
      // is invalid (docs/06 §4.1).
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

// ── Card ────────────────────────────────────────────────────────────────────────────────────
export const Card = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('rounded border border-border bg-card', className)} {...props} />
);

export const CardHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1 p-5', className)} {...props} />
);

export const CardTitle = ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cn('text-base font-semibold leading-none tracking-tight', className)} {...props} />
);

export const CardDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-muted-foreground', className)} {...props} />
);

export const CardContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-5 pt-0', className)} {...props} />
);

export const CardFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center gap-2 p-5 pt-0', className)} {...props} />
);

// ── Badge ───────────────────────────────────────────────────────────────────────────────────
/**
 * Status colour language, consistent everywhere (docs/06 §7):
 *   gray = draft/pending · green = active/completed · amber = awaiting action · red = failed/
 *   blocked/cancelled.
 * Status is NEVER conveyed by colour alone (a11y §12) — every badge carries text.
 */
const badgeVariants = cva(
  // Square-ish pills, exactly like the admin's StatusBadge — same tints, same radius.
  'inline-flex items-center gap-1 whitespace-nowrap rounded border px-2 py-0.5 text-xs font-medium leading-5',
  {
    variants: {
      tone: {
        neutral: 'border-slate-200 bg-slate-50 text-slate-700',
        green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        amber: 'border-amber-200 bg-amber-50 text-amber-800',
        red: 'border-red-200 bg-red-50 text-red-700',
        muted: 'border-border bg-background text-muted-foreground',
        blue: 'border-blue-200 bg-blue-50 text-blue-700',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = ({ className, tone, ...props }: BadgeProps) => (
  <span className={cn(badgeVariants({ tone }), className)} {...props} />
);

// ── Input / Textarea ────────────────────────────────────────────────────────────────────────
// There is deliberately no `Select` here. It was a bare <select>, and its option rows were drawn
// by the OS — unstylable, and visibly foreign on this palette. The custom listbox in `@most/ui`
// replaced it; `SelectField` in ./form.tsx is the only way this app builds a select.
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm transition-colors',
        'placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0',
        'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
        'aria-[invalid=true]:border-red-400 aria-[invalid=true]:focus-visible:border-red-400 aria-[invalid=true]:focus-visible:ring-red-200',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
      'placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0',
      'aria-[invalid=true]:border-red-400 aria-[invalid=true]:focus-visible:border-red-400 aria-[invalid=true]:focus-visible:ring-red-200',
      className,
    )}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

// ── Skeleton ────────────────────────────────────────────────────────────────────────────────
/** Loading state = skeletons matching the final layout, never a bare spinner (docs/06 §4). */
export const Skeleton = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('skeleton h-4 w-full', className)} aria-hidden {...props} />
);

// ── Separator ───────────────────────────────────────────────────────────────────────────────
export const Separator = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div role="separator" className={cn('h-px w-full bg-border', className)} {...props} />
);
